import { API_BASE } from '@/shared/services/api';
import { useTranslation } from "react-i18next";import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/components/ui/card';
import { Button } from '@/shared/components/ui/button';
import { Badge } from '@/shared/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/shared/components/ui/tabs';
import { toast } from 'sonner';
import {
  Download,
  TrendingUp,
  TrendingDown,
  DollarSign,
  Clock,
  Users,
  FileText,
  BarChart3,
  PieChart,
  Calendar,
  RefreshCw } from
'lucide-react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  LineChart,
  Line,
  PieChart as RechartsPie,
  Pie,
  Cell } from
'recharts';

const API_URL = API_BASE;

const getAuthHeaders = () => {
  const token = localStorage.getItem('ezerpToken');
  return {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${token}`
  };
};

const COLORS = ['#f97316', '#14b8a6', '#3b82f6', '#eab308', '#8b5cf6'];

export const ProcurementReports = () => {const { t } = useTranslation("procurement");
  const [activeTab, setActiveTab] = useState('overview');
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchReportData = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/procurement/reports/summary`, {
        headers: getAuthHeaders()
      });
      if (!response.ok) throw new Error('Failed to fetch report data');
      const data = await response.json();
      setReportData(data);
    } catch (err) {
      toast.error('Failed to load procurement reports');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReportData();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-orange-500" />
      </div>);

  }

  const spendByCategory = reportData?.spend_by_category || [];
  const vendorPerformance = reportData?.vendor_performance || [];
  const monthlySpend = reportData?.monthly_spend || [];
  const kpis = reportData?.kpis || {};

  return (
    <div className="space-y-6" data-testid="procurement-reports">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight font-['Space_Grotesk']">{t("procurementReports", "Procurement Reports")}

          </h1>
          <p className="text-muted-foreground mt-1">{t("analyticsAndInsights", "Analytics and insights for procurement operations")}

          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={fetchReportData} className="gap-2">
            <RefreshCw className="h-4 w-4" />{t("refresh", "Refresh")}

          </Button>
          <Button variant="outline" className="gap-2" onClick={() => toast.info('Time range selector placeholder')}>
            <Calendar className="h-4 w-4" />{t("thisYear", "This Year")}

          </Button>
          <Button variant="outline" className="gap-2" onClick={() => toast.info('Export started...')}>
            <Download className="h-4 w-4" />{t("export", "Export")}

          </Button>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid gap-4 sm:grid-cols-4">
        <Card className="card-elevated border-l-4 border-l-orange-500">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-orange-500/10">
                <DollarSign className="h-5 w-5 text-orange-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">{t("totalSpendYtd", "Total Spend YTD")}</p>
                <p className="text-2xl font-bold">${(kpis.total_spend_ytd / 1000000).toFixed(2)}M</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="card-elevated border-l-4 border-l-teal-500">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-teal-500/10">
                <Clock className="h-5 w-5 text-teal-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">{t("avgPoCycle", "Avg PO Cycle Time")}</p>
                <p className="text-2xl font-bold">{kpis.avg_po_cycle_time}{t("days", "days")}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="card-elevated border-l-4 border-l-blue-500">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-blue-500/10">
                <Users className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">{t("activeVendors", "Active Vendors")}</p>
                <p className="text-2xl font-bold">{kpis.vendor_count}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="card-elevated border-l-4 border-l-emerald-500">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-emerald-500/10">
                <FileText className="h-5 w-5 text-emerald-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">{t("activeContracts", "Active Contracts")}</p>
                <p className="text-2xl font-bold">{kpis.active_contracts}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="overview">{t("overview", "Overview")}</TabsTrigger>
          <TabsTrigger value="spend">{t("spendAnalysis", "Spend Analysis")}</TabsTrigger>
          <TabsTrigger value="vendors">{t("vendorPerformance", "Vendor Performance")}</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-6">
          <div className="grid gap-6 lg:grid-cols-2">
            {/* Monthly Spend Trend */}
            <Card className="card-elevated">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <TrendingUp className="h-5 w-5 text-muted-foreground" />{t("monthlySpendTrend", "Monthly Spend Trend")}

                </CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={250}>
                  <LineChart data={monthlySpend}>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                    <XAxis dataKey="month" stroke="hsl(var(--muted-foreground))" fontSize={12} />
                    <YAxis stroke="hsl(var(--muted-foreground))" fontSize={12} tickFormatter={(v) => `$${v / 1000}k`} />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: 'hsl(var(--card))',
                        border: '1px solid hsl(var(--border))',
                        borderRadius: '8px'
                      }}
                      formatter={(value) => [`$${value.toLocaleString()}`, 'Spend']} />
                    
                    <Line type="monotone" dataKey="amount" stroke="#f97316" strokeWidth={2} dot={{ fill: '#f97316' }} />
                  </LineChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            {/* Spend by Category */}
            <Card className="card-elevated">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <PieChart className="h-5 w-5 text-muted-foreground" />{t("spendByCategory", "Spend by Category")}

                </CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={250}>
                  <RechartsPie>
                    <Pie
                      data={spendByCategory}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={100}
                      paddingAngle={2}
                      dataKey="amount"
                      nameKey="category">
                      
                      {spendByCategory.map((entry, index) =>
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      )}
                    </Pie>
                    <Tooltip
                      contentStyle={{
                        backgroundColor: 'hsl(var(--card))',
                        border: '1px solid hsl(var(--border))',
                        borderRadius: '8px'
                      }}
                      formatter={(value) => `$${value.toLocaleString()}`} />
                    
                  </RechartsPie>
                </ResponsiveContainer>
                <div className="flex flex-wrap justify-center gap-4 mt-4">
                  {spendByCategory.map((cat, idx) =>
                  <div key={cat.category} className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full" style={{ backgroundColor: COLORS[idx % COLORS.length] }} />
                      <span className="text-sm">{cat.category}</span>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="spend" className="space-y-6">
          <Card className="card-elevated">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <BarChart3 className="h-5 w-5 text-muted-foreground" />{t("categoryBreakdown", "Category Breakdown")}

              </CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={spendByCategory} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                  <XAxis type="number" stroke="hsl(var(--muted-foreground))" fontSize={12} tickFormatter={(v) => `$${v / 1000}k`} />
                  <YAxis type="category" dataKey="category" stroke="hsl(var(--muted-foreground))" fontSize={12} width={120} />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: 'hsl(var(--card))',
                      border: '1px solid hsl(var(--border))',
                      borderRadius: '8px'
                    }}
                    formatter={(value) => `$${value.toLocaleString()}`} />
                  
                  <Bar dataKey="amount" fill="#f97316" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          {/* Spend Table */}
          <Card className="card-elevated">
            <CardHeader>
              <CardTitle>{t("categoryDetails", "Category Details")}</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-border bg-muted/30">
                      <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">{t("category", "Category")}</th>
                      <th className="text-right py-3 px-4 text-sm font-medium text-muted-foreground">{t("amount", "Amount")}</th>
                      <th className="text-right py-3 px-4 text-sm font-medium text-muted-foreground">{t("ofTotal", "% of Total")}</th>
                      <th className="text-right py-3 px-4 text-sm font-medium text-muted-foreground">{t("yoyChange", "YoY Change")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {spendByCategory.map((cat, idx) => {
                      const total = spendByCategory.reduce((sum, c) => sum + c.amount, 0);
                      const percentage = (cat.amount / total * 100).toFixed(1);
                      return (
                        <tr key={cat.category} className="border-b border-border/50 hover:bg-muted/30 transition-colors">
                          <td className="py-4 px-4">
                            <div className="flex items-center gap-2">
                              <div className="w-3 h-3 rounded-full" style={{ backgroundColor: COLORS[idx % COLORS.length] }} />
                              <span className="font-medium">{cat.category}</span>
                            </div>
                          </td>
                          <td className="py-4 px-4 text-right font-mono">${cat.amount.toLocaleString()}</td>
                          <td className="py-4 px-4 text-right">{percentage}%</td>
                          <td className="py-4 px-4 text-right">
                            <span className={cat.change >= 0 ? 'text-red-600' : 'text-emerald-600'}>
                              {cat.change >= 0 ? <TrendingUp className="h-4 w-4 inline mr-1" /> : <TrendingDown className="h-4 w-4 inline mr-1" />}
                              {Math.abs(cat.change)}%
                            </span>
                          </td>
                        </tr>);

                    })}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="vendors" className="space-y-6">
          <Card className="card-elevated">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Users className="h-5 w-5 text-muted-foreground" />{t("vendorScorecard", "Vendor Scorecard")}

              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-border bg-muted/30">
                      <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">{t("vendor", "Vendor")}</th>
                      <th className="text-center py-3 px-4 text-sm font-medium text-muted-foreground">{t("ontimeDelivery", "On-Time Delivery")}</th>
                      <th className="text-center py-3 px-4 text-sm font-medium text-muted-foreground">{t("qualityScore", "Quality Score")}</th>
                      <th className="text-center py-3 px-4 text-sm font-medium text-muted-foreground">{t("costVariance", "Cost Variance")}</th>
                      <th className="text-center py-3 px-4 text-sm font-medium text-muted-foreground">{t("overall", "Overall")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {vendorPerformance.map((vendor) => {
                      const overallScore = ((vendor.on_time + vendor.quality) / 2).toFixed(0);
                      return (
                        <tr key={vendor.vendor} className="border-b border-border/50 hover:bg-muted/30 transition-colors">
                          <td className="py-4 px-4 font-medium">{vendor.vendor}</td>
                          <td className="py-4 px-4 text-center">
                            <div className="flex items-center justify-center gap-2">
                              <div className="w-24 h-2 bg-muted rounded-full overflow-hidden">
                                <div
                                  className={`h-full rounded-full ${vendor.on_time >= 90 ? 'bg-emerald-500' : vendor.on_time >= 80 ? 'bg-amber-500' : 'bg-red-500'}`}
                                  style={{ width: `${vendor.on_time}%` }} />
                                
                              </div>
                              <span className="text-sm font-medium">{vendor.on_time}%</span>
                            </div>
                          </td>
                          <td className="py-4 px-4 text-center">
                            <div className="flex items-center justify-center gap-2">
                              <div className="w-24 h-2 bg-muted rounded-full overflow-hidden">
                                <div
                                  className={`h-full rounded-full ${vendor.quality >= 95 ? 'bg-emerald-500' : vendor.quality >= 90 ? 'bg-amber-500' : 'bg-red-500'}`}
                                  style={{ width: `${vendor.quality}%` }} />
                                
                              </div>
                              <span className="text-sm font-medium">{vendor.quality}%</span>
                            </div>
                          </td>
                          <td className="py-4 px-4 text-center">
                            <Badge className={
                            vendor.cost_variance <= 0 ? 'bg-emerald-100 text-emerald-700' :
                            vendor.cost_variance <= 3 ? 'bg-amber-100 text-amber-700' :
                            'bg-red-100 text-red-700'
                            }>
                              {vendor.cost_variance > 0 ? '+' : ''}{vendor.cost_variance}%
                            </Badge>
                          </td>
                          <td className="py-4 px-4 text-center">
                            <Badge className={
                            overallScore >= 90 ? 'bg-emerald-100 text-emerald-700' :
                            overallScore >= 80 ? 'bg-amber-100 text-amber-700' :
                            'bg-red-100 text-red-700'
                            }>
                              {overallScore}
                            </Badge>
                          </td>
                        </tr>);

                    })}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>);

};